import { useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';

import AddRoundedIcon from '@mui/icons-material/AddRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import StarOutlineRoundedIcon from '@mui/icons-material/StarOutlineRounded';
import TranslateRoundedIcon from '@mui/icons-material/TranslateRounded';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Snackbar,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';

import { EmptyState, ErrorState, LoadingState, SectionScaffold, TablePageShell } from '@/shared/ui';

import { useCatalogLanguages, useDeleteCatalogLanguage, useUpdateCatalogLanguage } from '../../hooks';
import { useCatalogPermissions } from '../../lib/useCatalogPermissions';
import { useToast } from '../../lib/useToast';
import type { CatalogLanguage } from '../../model/types';
import { CatalogLanguageFormDialog } from '../components/CatalogLanguageFormDialog';

/** Catalog language registry — user-story-catalog-006. */
export function CatalogLanguagesPage() {
  const { t } = useTranslation('catalog');
  const { canStructureView, canStructureManage } = useCatalogPermissions();
  const { toast, success, error: notifyError, clear } = useToast();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [deletingLocale, setDeletingLocale] = useState<string | null>(null);

  const languagesQuery = useCatalogLanguages({ enabled: canStructureView });
  const updateMutation = useUpdateCatalogLanguage();
  const deleteMutation = useDeleteCatalogLanguage();

  const languages = useMemo(() => {
    const list = languagesQuery.data ?? [];
    return [...list].sort((a, b) => {
      if (a.defaultLocale !== b.defaultLocale) return a.defaultLocale ? -1 : 1;
      return a.name.localeCompare(b.name);
    });
  }, [languagesQuery.data]);

  const deleting = deletingLocale ? (languages.find((l) => l.locale === deletingLocale) ?? null) : null;

  const toggleActive = (language: CatalogLanguage) => {
    updateMutation.mutate(
      { locale: language.locale, body: { active: !language.active } },
      {
        onError: (e) => notifyError(e.message ?? t('languages.toggleActive.error')),
      },
    );
  };

  const setDefault = (language: CatalogLanguage) => {
    updateMutation.mutate(
      { locale: language.locale, body: { defaultLocale: true } },
      {
        onSuccess: () => success(t('languages.setDefault.success', { name: language.name })),
        onError: (e) => notifyError(e.message ?? t('languages.setDefault.error')),
      },
    );
  };

  const confirmDelete = () => {
    if (!deleting) return;
    const { locale, name } = deleting;
    deleteMutation.mutate(locale, {
      onSuccess: () => {
        success(t('languages.delete.success', { name }));
        setDeletingLocale(null);
      },
      onError: (e) => {
        notifyError(e.message ?? t('languages.delete.error'));
        setDeletingLocale(null);
      },
    });
  };

  return (
    <SectionScaffold
      title={t('languages.title')}
      subtitle={t('languages.subtitle')}
      canView={canStructureView}
      settingsTo="/settings/catalog"
      settingsLabel={t('languages.settingsLabel')}
      primaryAction={
        canStructureManage ? (
          <Button variant="contained" startIcon={<AddRoundedIcon />} onClick={() => setDialogOpen(true)}>
            {t('languages.actions.add')}
          </Button>
        ) : undefined
      }
    >
      <TablePageShell>
        {languagesQuery.isLoading ? (
          <LoadingState label={t('languages.loading')} />
        ) : languagesQuery.isError ? (
          <ErrorState
            title={t('languages.loadError.title')}
            message={languagesQuery.error?.message ?? t('common:error.unknown')}
            onRetry={() => void languagesQuery.refetch()}
          />
        ) : languages.length === 0 ? (
          <EmptyState
            title={t('languages.empty.title')}
            description={t('languages.empty.description')}
            icon={<TranslateRoundedIcon />}
            action={
              canStructureManage ? (
                <Button variant="contained" startIcon={<AddRoundedIcon />} onClick={() => setDialogOpen(true)}>
                  {t('languages.actions.add')}
                </Button>
              ) : undefined
            }
          />
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('languages.table.locale')}</TableCell>
                  <TableCell>{t('languages.table.name')}</TableCell>
                  <TableCell>{t('languages.table.default')}</TableCell>
                  <TableCell>{t('languages.table.active')}</TableCell>
                  <TableCell align="right">{t('languages.table.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {languages.map((language) => (
                  <TableRow key={language.locale} hover>
                    <TableCell>
                      <Box
                        component="code"
                        sx={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 13 }}
                      >
                        {language.locale}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {language.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {language.defaultLocale ? (
                        <Chip
                          size="small"
                          color="primary"
                          icon={<StarRoundedIcon />}
                          label={t('languages.defaultChip')}
                        />
                      ) : canStructureManage ? (
                        <Button
                          size="small"
                          startIcon={<StarOutlineRoundedIcon fontSize="small" />}
                          onClick={() => setDefault(language)}
                          disabled={updateMutation.isPending}
                        >
                          {t('languages.actions.setDefault')}
                        </Button>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          —
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {(() => {
                        const activeSwitch = (
                          <Switch
                            checked={language.active}
                            onChange={() => toggleActive(language)}
                            disabled={
                              !canStructureManage || updateMutation.isPending || language.defaultLocale
                            }
                            slotProps={{
                              input: {
                                'aria-label': t('languages.toggleActive.aria', { name: language.name }),
                              },
                            }}
                          />
                        );
                        // The default language can never be disabled (adr-tenant-005) — the server
                        // would 409; prevent it client-side with an explanatory tooltip instead of a
                        // bare disabled control. Only shown when the switch is disabled FOR THIS
                        // reason specifically — a viewer without manage rights sees no tooltip here,
                        // same as every other already-disabled control on this page.
                        return language.defaultLocale && canStructureManage ? (
                          <Tooltip title={t('languages.toggleActive.defaultDisabledTooltip')}>
                            <span>{activeSwitch}</span>
                          </Tooltip>
                        ) : (
                          activeSwitch
                        );
                      })()}
                    </TableCell>
                    <TableCell align="right">
                      {canStructureManage && (
                        <Tooltip title={t('languages.rowActions.delete')}>
                          <IconButton
                            size="small"
                            color="error"
                            aria-label={t('languages.rowActions.deleteAria', { name: language.name })}
                            onClick={() => setDeletingLocale(language.locale)}
                          >
                            <DeleteOutlineRoundedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </TablePageShell>

      <Dialog open={deletingLocale !== null} onClose={() => setDeletingLocale(null)}>
        <DialogTitle>{t('languages.delete.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t('languages.delete.body', { name: deleting?.name })}</DialogContentText>
          <Alert severity="info" sx={{ mt: 2 }}>
            {t('languages.delete.guardHint')}
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeletingLocale(null)}>{t('common:action.cancel')}</Button>
          <Button
            color="error"
            variant="contained"
            onClick={confirmDelete}
            disabled={deleteMutation.isPending}
          >
            {t('common:action.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      <CatalogLanguageFormDialog
        open={dialogOpen}
        hasExistingLanguages={languages.length > 0}
        onClose={() => setDialogOpen(false)}
        onResult={(severity, message) =>
          severity === 'success' ? success(message) : notifyError(message)
        }
      />

      <Snackbar
        open={toast !== null}
        autoHideDuration={5000}
        onClose={clear}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={clear}
          severity={toast?.severity ?? 'info'}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {toast?.message ?? ''}
        </Alert>
      </Snackbar>
    </SectionScaffold>
  );
}
